import { faMessages, faBlockQuestion, faEnvelope } from "@fortawesome/pro-light-svg-icons";
import { faArrowRight, faArrowLeft, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { useAuthentication } from "common/auth/hooks";
import LinkButton, { LinkButtonProps } from "common/components/LinkButton";
import Icon from "common/components/Icon";
import Image from "common/components/Image";
import { toPhone } from "common/utils/formatting";

import { useClinic, useFeatures } from "client/clinics/hooks";
import { INITIAL_CLIENT_DATA } from "client/common/utils";
import { useConsentPreferencesDialog } from "client/marketing/hooks/stackables";
import { ModalStatus } from "client/common/types";
import { externalUrls, urls } from "client/routes";
import { useFulfillerInfoDrawer, useShippingInfoDrawer } from "client/common/partners/stackables";
import { useSupportChat } from "client/support/hooks";

import Container from "../layouts/Container";
import nabp from "../../images/nabp-healthcare-merchant.png";
import Accordion from "../interactive/Accordion";
import TextButton from "../buttons/TextButton";

import styles from "../../styles/Footer.module.less";

const Footer = () => {
  const clinic = useClinic();
  const features = useFeatures();
  const { isAuthenticated } = useAuthentication();
  const chat = useSupportChat();
  const {
    vetcove_support_email: supportEmail,
    vetcove_support_hours: chatSupportHours,
    vetcove_phone_support_hours: phoneSupportHours,
    vetcove_support_phone: supportPhone,
  } = INITIAL_CLIENT_DATA;

  const { open: openShippingInfoDrawer } = useShippingInfoDrawer();
  const { open: openFulfillerInfoDrawer } = useFulfillerInfoDrawer();
  const { open: openConsentPreferencesDialog } = useConsentPreferencesDialog();

  const navigation: {
    label: string;
    links: (LinkButtonProps & { children: string })[];
    extra?: React.ReactNode;
  }[] = [
    {
      label: "Popular categories",
      links: clinic.footer_categories.map((category) => ({
        children: category.name,
        dest: urls.products.category({ params: { id: category.id } }),
      })),
      extra: [
        <LinkButton
          key="view-all"
          dest={urls.products.search()}
          className={cx(styles.item, styles.all)}
        >
          View all categories <Icon className={styles.angle} icon={faArrowRight} />
        </LinkButton>,
      ],
    },
    {
      label: "Storefront",
      links: [
        {
          children: "FAQs",
          dest: urls.support.helpCenter(),
        },
        {
          children: "Shipping policy",
          onClick: openShippingInfoDrawer,
        },
        {
          children: "Pharmacy partners",
          onClick: openFulfillerInfoDrawer,
        },
      ],
    },
    {
      label: "Account",
      links: [
        {
          children: "My account",
          dest: urls.accounts.detail(),
        },
        {
          children: "My prescriptions",
          dest: urls.prescriptions.list(),
        },
        {
          children: "My orders",
          dest: urls.orders.list(),
        },
        {
          children: "Track an order",
          dest: isAuthenticated
            ? urls.orders.list()
            : urls.support.helpCenter({ search: { orderStatusModalIsVisible: ModalStatus.Open } }),
        },
        // TODO: Enable once Mitch is ready
        // {
        //   label: 'Refill Prescription',
        //   to: urls.refills.requests.start(),
        // },
        ...(isAuthenticated
          ? [
              {
                children: "Sign out",
                href: externalUrls.signout(),
                newTab: false,
              },
            ]
          : [
              {
                children: "Log in",
                dest: urls.accounts.login(),
              },
              {
                children: "Sign up",
                dest: urls.accounts.signup(),
              },
            ]),
      ],
    },
  ];

  const helpLinks: (LinkButtonProps & { children: string; icon: IconDefinition })[] = [
    {
      dest: urls.support.helpCenter(),
      children: "FAQ",
      icon: faBlockQuestion,
    },
    {
      onClick: () => chat.show(),
      children: "Chat",
      icon: faMessages,
    },
    // {
    //   href: '/', // TODO: Get the clinic text phone number
    //   label: 'Text',
    //   icon: faMessageDots,
    // },
    {
      href: `mailto:${supportEmail}`,
      children: "Email",
      icon: faEnvelope,
    },
  ];

  const legalLinks: (LinkButtonProps & { children: string })[] = [
    {
      children: "Terms of use",
      dest: urls.support.termsOfUse(),
    },
    {
      children: "Privacy policy",
      dest: urls.support.privacyPolicy(),
    },
    {
      children: "Your privacy choices",
      onClick: openConsentPreferencesDialog,
    },
    {
      children: "Safe needle disposal",
      href: externalUrls.safeNeedleDisposal(),
    },
    {
      children: "Proper medication disposal",
      href: externalUrls.disposalOfUnusedMedicine(),
    },
  ];

  return (
    <div className={styles.main}>
      <Container>
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.navs}>
              <div className={styles.options}>
                {navigation.map((nav) => (
                  <div className={styles.nav} key={nav.label}>
                    <div className={styles.label}>{nav.label}</div>
                    <div className={styles.links}>
                      {nav.links.map(({ children, ...rest }) => (
                        <LinkButton {...rest} className={styles.item} key={children}>
                          {children}
                        </LinkButton>
                      ))}
                      {nav.extra}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.accordion}>
                <Accordion.Single
                  classNames={{ label: styles.label, item: styles.noBorder }}
                  items={navigation.map((nav) => ({
                    label: nav.label,
                    key: nav.label,
                    content: (
                      <div className={styles.links}>
                        {nav.links.map(({ children, ...rest }) => (
                          <LinkButton {...rest} className={styles.item} key={children}>
                            {children}
                          </LinkButton>
                        ))}
                        {nav.extra}
                      </div>
                    ),
                  }))}
                />
              </div>
            </div>
            <div className={styles.help}>
              <div>
                <p className={styles.label}>We&apos;re here to help</p>
                <p className={styles.sublabel}>(With paw-some online store support)</p>
              </div>
              <div className={styles.links}>
                {helpLinks.map(({ icon, children, ...other }) => (
                  <LinkButton key={children} className={styles.link} {...other}>
                    <div className={styles.button}>
                      <Icon className={styles.icon} fixedWidth={true} icon={icon} />
                    </div>
                    <div className={styles.info}>{children}</div>
                  </LinkButton>
                ))}
              </div>
              <div className={styles.finePrint}>
                <div>
                  This site is powered by OurVet, the online store for {clinic.display_name}, and
                  operated by Vetcove. For questions related to your order, contact Pet Parent
                  Support via chat ({chatSupportHours}), email{" "}
                  <TextButton href={`mailto:${supportEmail}`}>{supportEmail}</TextButton>, or call
                  or text{" "}
                  <TextButton href={`tel:${supportPhone}`}>{toPhone(supportPhone)}</TextButton> (
                  {phoneSupportHours}).
                </div>
                <div>
                  Questions for your veterinarian? Please call{" "}
                  {clinic.phone_number ? (
                    <TextButton href={`tel:${clinic.phone_number}`}>
                      {toPhone(clinic.phone_number)}
                    </TextButton>
                  ) : (
                    "your veterinary clinic"
                  )}
                  .
                </div>
              </div>
            </div>
          </div>
          <div className={styles.bottom}>
            <div className={styles.sites}>
              {features.STOREFRONT_HOMEPAGE_LINK && (
                <div className={styles.site}>
                  <LinkButton className={styles.button} href={clinic.website_url || "/"}>
                    <Icon className={styles.angle} icon={faArrowLeft} /> Return to main site
                  </LinkButton>
                </div>
              )}
              <div className={styles.nabpContainer}>
                <LinkButton href={externalUrls.nabp()} className={styles.nabp}>
                  <Image src={nabp} alt="NABP Healthcare Merchant Accreditation" />
                </LinkButton>
                <p className={styles.nabpDisclaimer}>
                  Vetcove has been accredited by the National Association of Boards of Pharmacy®
                  (NABP®) as a Healthcare Merchant. Any NABP accreditation of individual veterinary
                  clinics is separate and not implied by the display of this mark.
                </p>
              </div>
            </div>
            <div className={styles.fine}>
              <div className={styles.legal}>
                {legalLinks.map(({ children, ...other }) => (
                  <LinkButton className={styles.legalLink} key={children} {...other}>
                    {children}
                  </LinkButton>
                ))}
              </div>
              <div className={styles.copyright}>
                <p className={styles.text}>
                  © {new Date().getFullYear()} Vetcove Inc. All rights reserved.
                </p>
              </div>
            </div>
            <div className={styles.disclaimer}>
              <p>
                OurVet is an online storefront powered by Vetcove on behalf of your veterinarian.
                Vetcove is not a pharmacy and does not provide medical advice. Product information
                is for general reference only and should not replace guidance from your
                veterinarian. Please contact your veterinarian with any questions about medications
                or your pet&apos;s health.
              </p>
              <p>
                A valid veterinary-client-patient relationship (VCPR) is required to purchase
                prescription items. All prescription requests will be reviewed by your veterinarian.
                Orders will be filled by a licensed pharmacy partner and shipped to your home on
                behalf of your veterinarian. For more information about our pharmacy partners,
                please visit our FAQs or click the &quot;Pharmacy Partners&quot; link above. You
                have the right to fill prescriptions at the pharmacy of your choice.
              </p>
              <p>
                Your credit card statement will reflect a charge from Vetcove. For help with your
                order, please contact Pet Parent Support.
              </p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Footer;
